import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Alert, Button, Modal, Space, Spin, Tag, message } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import type { PDFDocumentProxy } from "pdfjs-dist";

import { templatesApi } from "../../../api/presentationsApi";
import { IPresentationTemplate } from "../../../types/models/presentations";
import { openPdf } from "./pdfPages";
import SlidePage from "./SlidePage";

/**
 * Which of a template's slides its teaser keeps.
 *
 * A teaser is the brochure with slides left out, never different content, so
 * choosing one is choosing slides. Every brochure from the template is cut
 * down to them, now and after every render.
 *
 * Slides are stored by PowerPoint's id rather than position: a later upload
 * can add or move slides without the teaser shifting onto the wrong ones.
 * Thumbnails come from the template preview, which includes hidden slides, so
 * page N is slide N.
 */

const THUMB = 132;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${THUMB}px, 1fr));
  gap: 14px;
  max-height: 62vh;
  overflow-y: auto;
  padding: 2px;
`;

const Tile = styled.button<{ chosen: boolean }>`
  position: relative;
  padding: 4px;
  border: 2px solid ${({ chosen }) => (chosen ? "#1890ff" : "transparent")};
  border-radius: 6px;
  background: ${({ chosen }) => (chosen ? "#e6f7ff" : "#fafafa")};
  cursor: pointer;
  text-align: left;

  canvas { display: block; opacity: ${({ chosen }) => (chosen ? 1 : 0.45)}; }

  .label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 4px;
    font-size: 12px;
  }
  .check {
    position: absolute;
    top: 8px;
    right: 8px;
    font-size: 18px;
    color: #1890ff;
  }
`;

const TeaserSlidesModal = ({
  template, onClose, onSaved,
}: {
  template: IPresentationTemplate;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const ids = template.manifest?.slide_ids ?? [];
  const hidden = new Set(template.manifest?.hidden_slides ?? []);
  const [chosen, setChosen] = useState<Set<number>>(new Set(template.teaser_slide_ids ?? []));
  const [doc, setDoc] = useState<PDFDocumentProxy>();
  const [previewUrl, setPreviewUrl] = useState(template.preview_url);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rechecking, setRechecking] = useState(false);

  // A template nobody has previewed has no PDF to draw from; build it once.
  useEffect(() => {
    if (previewUrl || !ids.length) return;
    setLoadingPdf(true);
    templatesApi.preview(Number(template.id))
      .then(setPreviewUrl)
      .catch(() => message.error("Could not build the template preview."))
      .finally(() => setLoadingPdf(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl]);

  useEffect(() => {
    if (!previewUrl) return undefined;
    let live = true;
    let opened: PDFDocumentProxy | undefined;
    setLoadingPdf(true);
    openPdf(previewUrl)
      .then((result) => { opened = result; if (live) setDoc(result); })
      .catch(() => live && setDoc(undefined))
      .finally(() => live && setLoadingPdf(false));
    return () => { live = false; opened?.destroy(); };
  }, [previewUrl]);

  const toggle = (id: number) => setChosen((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const save = async (slideIds: number[]) => {
    setSaving(true);
    try {
      await templatesApi.update(Number(template.id), { teaser_slide_ids: slideIds });
      message.success(slideIds.length
        ? "Saved. Every brochure's teaser is being cut again."
        : "Saved. This template no longer has a teaser.");
      onSaved();
      onClose();
    } catch (e: any) {
      const detail = e?.response?.data;
      message.error(detail?.teaser_slide_ids?.[0] ?? detail?.detail ?? "Could not save the teaser.");
    } finally {
      setSaving(false);
    }
  };

  const recheck = async () => {
    setRechecking(true);
    try {
      await templatesApi.revalidate(Number(template.id));
      message.success("Checked. Open the teaser again to pick its slides.");
      onSaved();
      onClose();
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? "Could not re-check the template.");
    } finally {
      setRechecking(false);
    }
  };

  const picked = ids.filter((id) => chosen.has(id));
  // Page N is taken for slide N. A preview with another page count (one built
  // without its hidden slides) would put every later slide under the wrong
  // number, and a click would save a different slide than the one pictured.
  const misaligned = !!doc && doc.numPages !== ids.length;
  const missing = template.teaser_slides?.missing ?? 0;

  return (
    <Modal
      visible
      title={`Teaser — ${template.name}`}
      onCancel={onClose}
      width={980}
      footer={(
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <span>
            {ids.length > 0 && `${picked.length} of ${ids.length} slides`}
          </span>
          <Space>
            {(template.teaser_slide_ids ?? []).length > 0 && (
              <Button danger disabled={saving} onClick={() => save([])}>
                No teaser
              </Button>
            )}
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" loading={saving}
                    disabled={!ids.length || !picked.length || misaligned}
                    onClick={() => save(picked)}>
              Save
            </Button>
          </Space>
        </Space>
      )}
    >
      {!ids.length ? (
        <Alert
          type="info"
          showIcon
          message="This template was checked before slides could be told apart."
          description="Re-check it once, then pick the teaser's slides."
          action={<Button loading={rechecking} onClick={recheck}>Re-check</Button>}
        />
      ) : (
        <>
          <p style={{ marginTop: 0 }}>
            Click the slides the teaser keeps. It shows them exactly as the brochure
            does, cut from each brochure after it is generated.
          </p>
          {misaligned && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message={`The preview has ${doc!.numPages} pages for ${ids.length} slides, so the pictures do not match the slide numbers.`}
              description="Re-check the template to rebuild its preview, then pick the teaser's slides."
              action={<Button loading={rechecking} onClick={recheck}>Re-check</Button>}
            />
          )}
          {missing > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${missing} of the teaser's slides ${missing === 1 ? "is" : "are"} no longer in this template. Save to forget ${missing === 1 ? "it" : "them"}.`}
            />
          )}
          <Spin spinning={loadingPdf && !doc} tip="Loading slides…">
            <Grid style={{ ["--slide-aspect" as any]: String(template.aspect ?? 16 / 9) }}>
              {ids.map((id, index) => {
                const number = index + 1;
                const on = chosen.has(id);
                return (
                  <Tile key={id} type="button" chosen={on} onClick={() => toggle(id)}
                        aria-pressed={on} aria-label={`Slide ${number}`}>
                    <SlidePage doc={doc} page={number} width={THUMB - 12} />
                    {on && <CheckCircleFilled className="check" />}
                    <div className="label">
                      <span>{number}</span>
                      {hidden.has(number) && <Tag style={{ marginRight: 0 }}>Hidden</Tag>}
                    </div>
                  </Tile>
                );
              })}
            </Grid>
          </Spin>
        </>
      )}
    </Modal>
  );
};

export default TeaserSlidesModal;
